import {
  ReactElement,
  ChangeEvent,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  createFilterOptions,
  Tooltip,
  TextField,
  Button,
  Menu,
  Avatar,
  Stack,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown as MoreFiltersIcon,
  faTruckFast as VehicleIcon,
  faVectorSquare as LogisticsZonesIcon,
} from '@fortawesome/free-solid-svg-icons';
import { compact } from 'lodash';

import {
  Subscription,
  CustomAutocomplete,
  VehicleInfo,
  UserInfo,
  AccessFeature,
  LogisiticZoneInfo,
} from '~components/index';
import {
  OrdersFiltersMenuContent,
  OrdersFiltersMenuInputs,
  OrdersFiltersMenuActions,
  OrdersFiltersMenuIconButton,
  OrdersFiltersMenuIconButtonIcon,
} from '~components/OrdersFiltersMenu/styles';
import { useToggleMenu } from '~hooks/index';
import { ORDERS_GLOBALS_OPTIONS } from '~constants/ordersStatesOptions';
import { RoutesActionFilterChip } from '~modules/Routes/styles';
import { getDefaultAvatar } from '~utils/user';
import { PlanFeaturesTypes, Roles } from '~globals/types/enums';

import { DialogOrderSearchFilterMenuProps } from './types';
import { DialogOrderSearchFilterChip } from './styles';

const DialogOrderSearchFiltersMenu = ({
  filters,
  activeFilters,
  onChangeFilterInput,
  onResetFilters,
  onApplyFilters,
  onClearFilters,
  selectsData,
}: DialogOrderSearchFilterMenuProps): ReactElement => {
  const [anchorMenuEl, openMenu, , handleToggleOpenMenu] = useToggleMenu();

  const [showTooltip, setShowTooltip] = useState(false);

  const handleShowTooltip = useCallback(() => {
    if (!openMenu) {
      setShowTooltip(true);
    }
  }, [openMenu]);

  useEffect(() => {
    if (openMenu) {
      setShowTooltip(false);
    }
  }, [openMenu]);

  const handleChangeInputFilter = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target ?? event.currentTarget;

      onChangeFilterInput(name, value);
    },
    [onChangeFilterInput],
  );

  const handleChangeInputDateFilter = useCallback(
    (fieldName: string) =>
      (value: Date | null): void => {
        onChangeFilterInput(fieldName, value);
      },
    [onChangeFilterInput],
  );

  const handleApplyFilter = useCallback(() => {
    onApplyFilters();
    handleToggleOpenMenu(false);
  }, [onApplyFilters, handleToggleOpenMenu]);

  const handleClearFilters = useCallback(() => {
    onClearFilters();
  }, [onClearFilters]);

  const handleClose = useCallback(() => {
    handleToggleOpenMenu(false);

    onResetFilters();
  }, [handleToggleOpenMenu, onResetFilters]);

  const isValidToApply = useMemo(
    () => !(JSON.stringify(filters) === JSON.stringify(activeFilters)),
    [filters, activeFilters],
  );

  const isEmptyFilters = useMemo(() => {
    const currentFilters = [
      filters.search,
      filters.expirationDate,
      filters.dateFrom,
      filters.dateTo,
      filters.states?.length,
      filters.vehicles?.length,
      filters.drivers?.length,
      filters.services?.length,
      filters.zones?.length,
      filters.priceGt,
      filters.priceLt,
      filters.assignedUser,
    ];

    const compactCurrentFilters = compact(currentFilters);

    return compactCurrentFilters.length === 0;
  }, [filters]);

  return (
    <>
      <Tooltip
        title="Mas filtros"
        arrow
        open={showTooltip}
        onOpen={handleShowTooltip}
        onClose={() => setShowTooltip(false)}
      >
        <OrdersFiltersMenuIconButton
          color="primary"
          variant="contained"
          ref={anchorMenuEl}
          aria-label="orders-filters"
          aria-controls={openMenu ? 'orders-filters-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openMenu ? 'true' : undefined}
          onClick={() => handleToggleOpenMenu(true)}
        >
          <OrdersFiltersMenuIconButtonIcon icon={MoreFiltersIcon} />
        </OrdersFiltersMenuIconButton>
      </Tooltip>

      <Menu
        id="orders-filters-menu"
        anchorEl={anchorMenuEl.current}
        open={openMenu}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        keepMounted
      >
        <OrdersFiltersMenuContent>
          <OrdersFiltersMenuInputs>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                &#8288;Precio
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Mayor a"
                  name="priceGt"
                  value={filters.priceGt ?? ''}
                  onChange={handleChangeInputFilter}
                  type="number"
                  onKeyDown={(event) => event.stopPropagation()}
                  fullWidth
                />

                <Typography
                  variant="h6"
                  color="text.secondary"
                  fontWeight={400}
                >
                  -
                </Typography>

                <TextField
                  label="Menor a"
                  name="priceLt"
                  value={filters.priceLt ?? ''}
                  onChange={handleChangeInputFilter}
                  type="number"
                  onKeyDown={(event) => event.stopPropagation()}
                  fullWidth
                />
              </Stack>
            </Stack>

            <Subscription.DataRetentionMin.DatePicker
              label="Fecha de vencimiento"
              value={filters.expirationDate}
              onChange={handleChangeInputDateFilter('expirationDate')}
              renderInput={(props) => <TextField {...props} />}
            />

            <Subscription.DataRetentionMin.DatePicker
              label="Desde"
              value={filters.dateFrom ?? null}
              onChange={handleChangeInputDateFilter('dateFrom')}
              renderInput={(props) => <TextField {...props} />}
            />

            <Subscription.DataRetentionMin.DatePicker
              label="Hasta"
              value={filters.dateTo ?? null}
              onChange={handleChangeInputDateFilter('dateTo')}
              renderInput={(props) => <TextField {...props} />}
            />

            <CustomAutocomplete
              innerRef={selectsData.states.autocompleteRef}
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              limitTags={1}
              renderTags={(vals, getTagsProps) =>
                vals.map((val, index) => (
                  // eslint-disable-next-line react/jsx-key
                  <DialogOrderSearchFilterChip
                    label={val.label as string}
                    size="small"
                    {...getTagsProps({ index })}
                  />
                ))
              }
              disableClearable
              options={ORDERS_GLOBALS_OPTIONS}
              label="Estados"
              name="states"
              onChange={(_e, vals) => {
                const parseVals = vals.map((val) => val.value);
                onChangeFilterInput('states', parseVals);
              }}
              getOptionLabel={(option) => option.label as string}
              sx={{ width: 318 }}
            />

            <CustomAutocomplete
              innerRef={selectsData.vehicles.autocompleteRef}
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              limitTags={1}
              renderTags={(vals, getTagsProps) =>
                vals.map((val, index) => (
                  // eslint-disable-next-line react/jsx-key
                  <RoutesActionFilterChip
                    label={`${val?.displayName}${
                      val?.code ? ` ${val.code}` : ''
                    }`}
                    icon={
                      <FontAwesomeIcon
                        icon={VehicleIcon}
                        style={{ color: val.color }}
                      />
                    }
                    size="small"
                    {...getTagsProps({ index })}
                  />
                ))
              }
              disableClearable
              options={selectsData.vehicles.list}
              loading={selectsData.vehicles.loading}
              label="Vehículos"
              name="vehicles"
              onChange={(_e, vals) => {
                onChangeFilterInput('vehicles', vals);
              }}
              getOptionLabel={(option) =>
                `${option?.displayName}${option?.code ? ` ${option.code}` : ''}`
              }
              renderOption={(props, option) => (
                <li {...props}>
                  <VehicleInfo
                    vehicle={option.displayName}
                    vehicleCode={option.code as string}
                    color={option.color}
                  />
                </li>
              )}
              sx={{ width: 318 }}
            />

            <CustomAutocomplete
              innerRef={selectsData.drivers.autocompleteRef}
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              limitTags={1}
              renderTags={(vals, getTagsProps) =>
                vals.map((val, index) => (
                  // eslint-disable-next-line react/jsx-key
                  <RoutesActionFilterChip
                    avatar={
                      <Avatar
                        src={getDefaultAvatar(val.avatar ?? undefined)}
                        alt={val.displayName}
                      />
                    }
                    label={val.displayName}
                    size="small"
                    {...getTagsProps({ index })}
                  />
                ))
              }
              disableClearable
              options={selectsData.drivers.list}
              loading={selectsData.drivers.loading}
              label="Choferes"
              name="drivers"
              onChange={(_e, vals) => {
                onChangeFilterInput('drivers', vals);
              }}
              getOptionLabel={(option) => option.displayName}
              renderOption={(props, option) => (
                <li {...props}>
                  <UserInfo
                    name={option.displayName}
                    urlAvatar={getDefaultAvatar(option.avatar ?? undefined)}
                  />
                </li>
              )}
              filterOptions={createFilterOptions({
                ignoreAccents: true,
                ignoreCase: true,
                stringify: (option) => option.displayName.replace(',', ''),
              })}
              sx={{ width: 318 }}
            />

            <AccessFeature.AutoComplete
              validFeature={PlanFeaturesTypes.LimitNotPro}
              innerRef={selectsData.services.autocompleteRef}
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              limitTags={1}
              renderTags={(vals, getTagsProps) =>
                vals.map((val, index) => (
                  // eslint-disable-next-line react/jsx-key
                  <RoutesActionFilterChip
                    label={val.label.toString()}
                    size="small"
                    {...getTagsProps({ index })}
                  />
                ))
              }
              disableClearable
              options={selectsData.services.list}
              loading={selectsData.services.loading}
              label="Tipos de servicios"
              name="services"
              onChange={(_e, vals) => {
                onChangeFilterInput('services', vals);
              }}
              fullWidth
              getOptionLabel={(option) => option.label.toString()}
            />

            <AccessFeature.AutoComplete
              validFeature={PlanFeaturesTypes.MgmZones}
              innerRef={selectsData.logisticsZones.autocompleteRef}
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              limitTags={1}
              renderTags={(vals, getTagsProps) =>
                vals.map((val, index) => (
                  // eslint-disable-next-line react/jsx-key
                  <RoutesActionFilterChip
                    icon={
                      val.color ? (
                        <FontAwesomeIcon
                          icon={LogisticsZonesIcon}
                          style={{ color: val.color }}
                        />
                      ) : undefined
                    }
                    label={val.code}
                    size="small"
                    {...getTagsProps({ index })}
                  />
                ))
              }
              disableClearable
              options={selectsData.logisticsZones.list}
              loading={selectsData.logisticsZones.loading}
              label="Zonas logísticas"
              name="zones"
              onChange={(_e, vals) => {
                onChangeFilterInput('zones', vals);
              }}
              getOptionLabel={(option) =>
                option.description
                  ? `${option.code} ${option.description}`
                  : option.code
              }
              renderOption={(props, option) => (
                <li {...props}>
                  <LogisiticZoneInfo
                    name={option.code}
                    description={option.description}
                    color={option.color}
                    withTooltip={Boolean(option.color)}
                  />
                </li>
              )}
              fullWidth
            />

            <AccessFeature.AutoComplete
              validFeature={PlanFeaturesTypes.LimitNotPro}
              innerRef={selectsData.users.autocompleteRef}
              options={selectsData.users.list}
              loading={selectsData.users.loading}
              label="Usuario asignado"
              name="assignedUser"
              onChange={(_e, val) => {
                onChangeFilterInput('assignedUser', val);
              }}
              fullWidth
              getOptionLabel={(option) =>
                `${option.lastName}, ${option.firstName}`
              }
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.userId}
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <UserInfo
                    name={`${option.lastName}, ${option.firstName}`}
                    urlAvatar={option.avatarUrl || undefined}
                  />
                </li>
              )}
              startAdornment={(val) =>
                val && (
                  <Avatar
                    src={getDefaultAvatar(val.avatarUrl || undefined)}
                    alt={`${val.lastName}, ${val.firstName}`}
                    sx={{ width: 20, height: 20 }}
                  />
                )
              }
              groupBy={(option) => {
                if (option.roleId === Roles.Drive) {
                  return 'Choferes';
                }

                return 'Usuarios';
              }}
              filterOptions={createFilterOptions({
                ignoreAccents: true,
                ignoreCase: true,
                stringify: (option) =>
                  `${option.lastName}, ${option.firstName}`.replace(',', ''),
              })}
            />
          </OrdersFiltersMenuInputs>

          <OrdersFiltersMenuActions>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleApplyFilter}
              disabled={!isValidToApply}
            >
              Aplicar
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleClearFilters}
              disabled={isEmptyFilters}
            >
              Borrar Filtros
            </Button>
          </OrdersFiltersMenuActions>
        </OrdersFiltersMenuContent>
      </Menu>
    </>
  );
};

export default DialogOrderSearchFiltersMenu;
